<template
  ><section class="section">
    <b-loading isFullPage :active="isLoading" />
    <h1 class="title ml-10">
      {{ id !== "criar" ? General.EDIT_CATEGORY : General.CREATE_CATEGORY }}
    </h1>

    <ValidationObserver ref="category" v-slot="{ handleSubmit }">
      <div class="field column is-6-desktop ml-10 mb-0 pb-0">
        <span class="label has-text-color-8"
          >{{ General.CLONE_CATEGORY }}
          <span class="optional-label"> {{ General.OPTIONAL }}</span></span
        >
        <b-autocomplete
          v-model="categoryExisting"
          :data="filterCategoryList"
          autocomplete
          field="name"
          name="category"
          :placeholder="
            isFetching
              ? General.MESSAGES.LOADING_RECORDS
              : General.SEARCH_EXIST_CATEGORY
          "
          keepFirst
          :loading="isFetching"
          @typing="getCategory"
          @select="option => cloneCategory(option)"
        >
          <template slot="empty">
            <p v-if="!isFetching">
              {{ General.MESSAGES.NO_CATEGORY_FOUND.toUpperCase() }}
            </p>
            <b-loading v-else :isFullPage="false" :active="isFetching" />
          </template>
        </b-autocomplete>
      </div>

      <div class="is-12-desktop mb-0 pb-0 is-flex">
        <ValidationProvider
          v-slot="{ errors }"
          :rules="['emptyField', General.TYPE_NAME].join(':')"
          class="column is-6-desktop ml-10 mb-0 pb-0"
        >
          <b-field
            :label="General.NAME_CATEGORY"
            :type="errors.length ? 'is-refused' : ''"
            customClass="has-text-color-8"
          >
            <b-input
              v-model="categoryName"
              :placeholder="General.PLACEHOLDER.INSERT_CATEGORY_NAME"
            />
            <template #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>

        <b-field
          v-if="id !== 'criar'"
          :label="General.CODE_CATEGORY"
          class="column is-3-desktop ml-10 mb-0 pb-0"
          customClass="has-text-color-8"
        >
          <b-input v-model="categoryCode" disabled />
        </b-field>
      </div>

      <div class="field column ml-10 mb-0 pb-0">
        <span class="label has-text-color-8"
          >{{ General.DESCRIPTION_CATEGORY }}
          <span class="optional-label"> {{ General.OPTIONAL }}</span>
        </span>
        <div>
          <b-input
            v-model="descriptionCategory"
            type="textarea"
            :placeholder="General.PLACEHOLDER.INSERT_CATEGORY_DESCRIPTION"
          />
        </div>
      </div>

      <div class="field column is-12-desktop pl-20 pb-0">
        <span class="label has-text-color-8"
          >{{ General.INDEX_CATEGORY }}
          <span class="optional-label"> {{ General.OPTIONAL }}</span>
        </span>
        <div>
          <CustomTagInput
            v-model="indexCategory"
            :beforeAdding="beforeAddingIndex"
            :data="hierarchyIndex(filterTagIndex)"
            autocomplete
            name="index"
            field="name"
            :placeholder="General.PLACEHOLDER.SEARCH_CATEGORY_INDEX"
            rounded
            @typing="setFilterTagIndex"
          >
            <template slot-scope="props">
              <span :class="{ 'custom-class-add': !props.option.id }">{{
                props.option.name
              }}</span>
            </template>
          </CustomTagInput>
        </div>
      </div>

      <div class="columns mx-10 mb-0">
        <b-field
          :label="General.PRIORITY_LEVEL"
          class="column is-3 pr-0 pb-0"
          customClass="has-text-color-8"
        >
          <b-dropdown
            v-model="prioritySelected"
            :mobileModal="false"
            aria-role="list"
            class="dropdown-field"
            expanded
          >
            <b-button
              slot="trigger"
              slot-scope="{ active }"
              class="config-select custom-dropdown-button"
              type="is-secondary"
              :iconRight="active ? 'chevron-up' : 'chevron-down'"
              outlined
              expanded
            >
              <span>{{ prioritySelected.name }}</span>
            </b-button>
            <b-dropdown-item
              v-for="(priority, index) in priorities"
              :key="index"
              :value="priority"
              aria-role="priorityItem"
            >
              <span class="has-text-weight-semibold">{{ priority.name }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </b-field>

        <b-field
          :label="General.ACCESS_LEVEL"
          class="column is-3 pr-0 pb-0"
          customClass="has-text-color-8"
        >
          <b-dropdown
            v-model="secrecySelected"
            :mobileModal="false"
            aria-role="list"
            class="dropdown-field"
            expanded
          >
            <b-button
              slot="trigger"
              slot-scope="{ active }"
              class="config-select custom-dropdown-button"
              type="is-secondary"
              :iconRight="active ? 'chevron-up' : 'chevron-down'"
              outlined
              expanded
            >
              <span>{{ secrecySelected.name }}</span>
            </b-button>
            <b-dropdown-item
              v-for="(secrecy, index) in selectableSecrecies"
              :key="index"
              :value="secrecy"
              aria-role="secrecyItem"
            >
              <span class="has-text-weight-semibold">{{ secrecy.name }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </b-field>

        <b-field
          v-if="isConfidential"
          class="column is-2 pr-0 pb-0"
          customClass="has-text-color-8"
        >
          <template #label>
            <span class="subtitle is-6 has-text-weight-semibold">
              {{ General.CONFIDENTIAL_PERIOD }}
            </span>
            <b-tooltip
              multilined
              role="tooltip"
              :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
              :size="isMobile ? 'is-medium' : 'is-large'"
              :position="isMobile ? 'is-top' : 'is-right'"
            >
              <template v-slot:content>
                <h6 class="has-text-weight-semibold is-flex">
                  {{ App.MenuMain.HELP }}
                </h6>
                <p class="has-text-left">
                  {{ General.TEXTS.CONFIDENTIAL_PERIOD }}
                </p>
              </template>
              <div class="is-flex">
                <b-icon
                  class="spacing-ml-10"
                  :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                  size="is-small"
                  icon="information"
                />
              </div>
            </b-tooltip>
          </template>
          <b-dropdown
            id="period_secrecy_subject"
            v-model="confidentialityPeriod"
            data-cy="PERIOD_CONFIDENTIAL"
            aria-role="list"
            scrollable
            maxHeight="200"
            class="dropdown-field is-full-child"
          >
            <b-button
              slot="trigger"
              slot-scope="{ active }"
              class="subject-config-select has-text-left"
              type="is-secondary"
              :iconRight="active ? 'chevron-up' : 'chevron-down'"
              outlined
            >
              <span>{{ confidentialityPeriod.description }}</span>
            </b-button>
            <b-dropdown-item
              v-for="period in listPeriod"
              :key="`period-${period.ref}`"
              :value="period"
              aria-role="priorityItem"
              role="option"
            >
              <span class="has-text-weight-semibold">{{
                period.description
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </b-field>

        <ValidationProvider
          v-slot="{ errors }"
          :disabled="disableCheckSecrecy"
          :rules="['emptyField', General.INCLUDE_LEGAL_REFERENCE].join(':')"
          class="full-width"
        >
          <b-field
            :type="!disableCheckSecrecy && errors.length ? 'is-refused' : ''"
            class="column pr-0 full-width"
            customClass="has-text-color-8"
          >
            <template #label>
              <label class="mr-10">{{ General.LEGAL_REFERENCE_ACCESS }}</label>
              <b-tooltip
                multilined
                role="tooltip"
                :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                :size="isMobile ? 'is-medium' : 'is-large'"
                position="is-top"
              >
                <template #content>
                  <h6 class="has-text-weight-semibold is-flex">
                    {{ App.MenuMain.HELP }}
                  </h6>
                  <p class="has-text-left">
                    {{ General.TEXTS.DESCRIPTION_LEGAL_REFERENCE }}
                  </p>
                </template>
                <div class="is-flex">
                  <b-icon
                    :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                    size="is-small"
                    icon="information"
                  />
                </div>
              </b-tooltip>
            </template>
            <CustomTagInput
              ref="legalReference"
              v-model="legal_reference"
              :data="listReferences"
              autocomplete
              :beforeAdding="beforeAddingLegalReference"
              field="description_trunc"
              name="legalReference"
              :placeholder="General.PLACEHOLDER.SEARCH_LEGISLATION"
              class="is-10 has-tag-ellipsis"
              rounded
              @typing="setLegalReference"
              @add="newLegalReference"
            >
              <template slot-scope="props">
                <span :class="{ 'custom-class-add': !props.option.id }">{{
                  props.option.description
                }}</span>
              </template>
              <template #selected="{ tags }">
                <b-tag
                  v-for="(tag, index) in tags"
                  :key="index"
                  rounded
                  closable
                  :aria-close-label="
                    `${General.ARIA_LABEL.DELETE_FIELD} ${tag.description}`
                  "
                  @close="
                    $refs.legalReference.$refs.tagInput.removeTag(index, $event)
                  "
                >
                  <div class="is-flex is-align-items-center">
                    <b-tooltip
                      :label="tag.description"
                      :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                      :size="isMobile ? 'is-small' : 'is-large'"
                      role="tooltip"
                      position="is-bottom"
                      multilined
                    >
                      <div class="tag-text is-flex">
                        <span class="has-ellipsis">
                          {{ tag.description }}
                        </span>
                      </div>
                    </b-tooltip>
                  </div>
                </b-tag>
              </template>
            </CustomTagInput>
            <template v-if="!disableCheckSecrecy" #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>
      </div>

      <div class="field columns ml-10 pb-0">
        <b-field class="column is-3">
          <b-checkbox
            v-model="allParts"
            :type="isContrast ? 'is-white' : 'is-blue-oil'"
            data-cy="check-all-part"
            class="is-size-6 has-text-weight-semibold has-text-color-7"
            >{{ General.APPLY_ALL_ORGANS }}
          </b-checkbox>
        </b-field>
        <b-field class="column is-3">
          <b-checkbox
            v-model="openAnotherSystem"
            :type="isContrast ? 'is-white' : 'is-blue-oil'"
            data-cy="check-open-out-system"
            class="is-size-6 has-text-weight-semibold has-text-color-7"
            >{{ General.OPENING_PROCESS_OUT_SYSTEM }}
          </b-checkbox>
        </b-field>
      </div>

      <ValidationProvider
        v-slot="{ errors }"
        :disabled="allParts"
        :rules="['emptyField', General.SELECT_TO].join(':')"
        class="mb-0 pb-0"
      >
        <b-field
          class="mx-10"
          :type="!allParts && errors.length ? 'is-refused' : ''"
        >
          <div class="is-flex is-flex-direction-column ml-10">
            <label class="is-size-6 has-text-weight-semibold mb-5">
              {{ General.SELECT_ORGAN_ENTITIES }}
            </label>
            <CustomTagInput
              v-model="part"
              :disabled="allParts"
              :beforeAdding="beforeAddingPart"
              :data="filteredParts(filterTagPart)"
              autocomplete
              name="part"
              field="name"
              class="pr-0"
              :placeholder="General.PLACEHOLDER.SEARCH_ORGAN_ENTITY"
              rounded
              expanded
              @typing="setFilterTagPart"
            >
              <template slot-scope="props">
                <span>{{ props.option.name }}</span>
              </template>

              <template slot="empty">
                <p>
                  {{ General.MESSAGES.NO_ORGAN_ENTITY.toUpperCase() }}
                </p>
              </template>
            </CustomTagInput>
          </div>
          <template v-if="!allParts" #message>
            <ErrorField :errors="errors" />
          </template>
        </b-field>
      </ValidationProvider>

      <div class="column mb-0 pb-0 row">
        <b-field class="column is-3">
          <b-checkbox
            v-model="blockSecrecyDown"
            data-cy="block-down-level"
            :type="isContrast ? 'is-white' : 'is-blue-oil'"
            class="is-size-6 has-text-weight-semibold has-text-color-7"
            >{{ General.BLOCK_DOWN_LEVEL }}
          </b-checkbox>
        </b-field>
      </div>

      <b-field>
        <div class="is-flex is-flex-direction-column ml-20 mr-10">
          <label class="is-size-6 has-text-weight-semibold mb-5">{{
            General.LABEL_LOW_LEVEL
          }}</label>
          <CustomTagInput
            v-model="lowLevelResponsible"
            :beforeAdding="beforeAddingLowLevelResponsible"
            :data="filteredParts(filterTagLowLevelResponsible)"
            autocomplete
            name="lowLevelResponsible"
            field="name"
            class="pr-0"
            :placeholder="General.PLACEHOLDER.SEARCH_ORGAN_ENTITY"
            rounded
            @typing="setFilterTagLowLevelResponsible"
          >
            <template slot-scope="props">
              <span>{{ props.option.name }}</span>
            </template>

            <template slot="empty">
              <p>
                {{ General.MESSAGES.NO_ORGAN_ENTITY.toUpperCase() }}
              </p>
            </template>
          </CustomTagInput>
        </div>
      </b-field>

      <div v-if="id === 'criar'" class="column ml-10 mb-0 pb-0 row">
        <b-button
          :type="isContrast ? 'is-white' : 'is-green-lawn'"
          iconLeft="plus"
          :expanded="isMobile"
          data-cy="create-subcategory"
          class="has-text-weight-semibold"
          @click="handleSubmit(() => createOrUpdate(true))"
        >
          {{ General.SUBCATEGORY }} 1
        </b-button>
      </div>

      <div class="column is-12-desktop mb-0 pb-0 row">
        <div class="custom-buttons p-10">
          <b-button
            :type="isContrast ? 'is-white' : 'is-green-lawn'"
            :expanded="isMobile"
            class="has-text-weight-semibold"
            data-cy="save-category"
            @click="handleSubmit(createOrUpdate)"
          >
            {{ id !== "criar" ? General.EDIT : General.SAVE }}
          </b-button>
          <b-button
            :type="isContrast ? 'is-white' : 'is-orange'"
            class="is-outlined"
            :class="{ 'ml-20': !isMobile }"
            :expanded="isMobile"
            @click="close"
          >
            {{ General.CANCEL }}
          </b-button>
        </div>
      </div>
    </ValidationObserver>
  </section>
</template>

<script>
import { debounce } from "lodash";
import { mapActions, mapGetters } from "vuex";
import { App, General } from "@/utils/constants";

import ModalAddIndexer from "./ModalAddIndexer.vue";
import {
  USER_PERMISSIONS as userPermissions,
  PRIORITY as priorities,
  SECRECY as secrecies
} from "@/utils/enumerate";
import ModalCreateLegalReference from "@/views/App/ModalCreateLegalReference.vue";

export default {
  name: "Category",

  props: {
    id: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      App,
      General,
      priorities,
      secrecies,
      categoryName: "",
      descriptionCategory: "",
      categoryExisting: "",
      nameSubcategory: "",
      indexCategory: [],
      part: [],
      legal_reference: [],
      lowLevelResponsible: [],
      filterTagIndex: "",
      filterTagPart: "",
      filterLegalReference: "",
      filterTagLowLevelResponsible: "",
      confidentialityPeriod: { description: General.SELECT_LEVEL_PERIOD },
      prioritySelected: { name: General.SELECT_PRIORITY },
      secrecySelected: { name: General.SELECT_LEVEL },
      allParts: false,
      openAnotherSystem: false,
      blockSecrecyDown: false,
      isLoading: false,
      isFetching: false,
      filterCategoryList: [],
      categoryList: [],
      categoryCode: ""
    };
  },

  computed: {
    ...mapGetters({
      filteredParts: "getFilteredParts",
      getParts: "getParts",
      indexerList: "getIndexer",
      isMobile: "isMobile",
      getFilteredLegalReference: "getFilteredLegalReference",
      getLegalReference: "getLegalReference",
      isConfidentialCheck: "isConfidentialCheck",
      hasPermission: "hasPermission",
      isContrast: "isContrast"
    }),

    hasPeriod() {
      return this.confidentialityPeriod.ref;
    },

    isConfidential() {
      return (
        this.isConfidentialCheck(this.secrecySelected.id) && this.hasPeriod
      );
    },

    listPeriod() {
      const maxPeriod = this.secrecySelected?.id
        ? this.getSecreciesById(this.secrecySelected.id).period
        : 0;

      return Array.from({ length: maxPeriod }, (_, index) => ({
        description: `${index + 1} ${index === 0 ? "ano" : "anos"}`,
        ref: index + 1
      }));
    },

    hasPermissionCreateLegalReference() {
      return this.hasPermission(userPermissions["CREATE_LEGAL_REFERENCE"]);
    },

    listReferences() {
      const list = this.getFilteredLegalReference(
        this.filterLegalReference,
        50
      );
      if (this.hasPermissionCreateLegalReference)
        list.push({
          description: `+ ${this.General.ADD_NEW_LEGAL_REFERENCE}`,
          id: 0
        });
      return list;
    },

    requestBaseData() {
      return {
        page: 1,
        paginator_count: 10,
        levels: JSON.stringify([1])
      };
    },

    disableCheckSecrecy() {
      return this.secrecySelected.id === 1;
    },

    selectableSecrecies() {
      return this.secrecies.filter(secrecy => secrecy.selectable);
    }
  },

  watch: {
    secrecySelected(secrecy) {
      const period = this.getSecrecyPeriod(secrecy.id);
      this.updateConfidentialityPeriod(period);
    }
  },

  async beforeMount() {
    this.isLoading = true;
    await this.legalReferenceRequestIfNotExist();
    if (this.id !== "criar") {
      const option = await this.categoryRequestById(this.id);
      this.categoryCode = option.code;
      this.cloneCategory(option, false);
    }
    if (!this.indexerList.length) {
      await this.indexerRequest();
    }
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      createCategory: "createCategory",
      updateCategory: "updateCategory",
      createLegalReference: "legalReferenceCreate",
      categoryRequestById: "categoryRequestById",
      categoryRequestByLevel: "categoryRequestByLevel",
      indexerRequest: "indexerRequest",
      legalReferenceRequestIfNotExist: "legalReferenceRequestIfNotExist"
    }),

    getSecrecyPeriod(secrecyId) {
      const secrecy = this.getSecreciesById(secrecyId);
      return secrecy.period;
    },

    updateConfidentialityPeriod(period) {
      this.confidentialityPeriod = {
        description: `${period || 0} ${period === 1 ? "ano" : "anos"}`,
        ref: period
      };
    },

    beforeAddingIndex(tag) {
      if (tag.id === 0) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalAddIndexer,
          hasModalCard: true,
          trapFocus: true
        });

        return false;
      }
      return !this.indexCategory.find(tagIndex => tagIndex === tag, false);
    },

    getCategory: debounce(async function(text) {
      this.isFetching = true;
      this.filterCategoryList = await this.categoryRequestByLevel({
        search: text,
        levels: JSON.stringify([1])
      });
      this.isFetching = false;
    }, 500),

    setLegalReference(text) {
      this.filterLegalReference = text;
    },

    setFilterTagPart(text) {
      this.filterTagPart = text;
    },

    beforeAddingLegalReference(tag) {
      if (tag.id === 0) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalCreateLegalReference,
          props: {
            create: tag => {
              this.newLegalReference(tag);
            }
          },
          hasModalCard: true,
          trapFocus: true
        });

        return false;
      }
      return !this.legal_reference.some(
        legalReference => legalReference.description === tag.description
      );
    },

    beforeAddingPart(tag) {
      return !this.part.find(part => part.id === tag.id, false);
    },

    setFilterTagIndex(text) {
      this.filterTagIndex = text;
    },

    beforeAddingLowLevelResponsible(tag) {
      return !this.lowLevelResponsible.find(part => part.id === tag.id, false);
    },

    setFilterTagLowLevelResponsible(text) {
      this.filterTagLowLevelResponsible = text;
    },

    setFilterTagOrganEntity(text) {
      this.filterTagOrganEntity = text;
    },

    hierarchyIndex(text) {
      const indexList = this.indexerList.filter(index =>
        index.name.toLowerCase().includes(text.toLowerCase())
      );
      return [...indexList, { id: 0, name: "+ Adicionar novo indexador" }];
    },

    async create() {
      if (this.validationBeforeRequest()) {
        const objCategory = {
          name: this.categoryName,
          description: this.descriptionCategory,
          level: 1,
          open_process: this.openAnotherSystem,
          block_param_low_level: this.blockSecrecyDown,
          indexers: this.indexCategory,
          all_parts: this.allParts,
          coverage_to_use: this.part.map(part => ({
            part_id: part.id,
            name: part.name
          })),
          priority: this.prioritySelected,
          low_level_responsible: this.lowLevelResponsible.map(part => ({
            part_id: part.id,
            name: part.name
          })),
          legal_reference: this.legal_reference,
          secrecy: this.secrecySelected,
          confidentiality_period: this.confidentialityPeriod.ref,
          conarq: null,
          parent: null
        };
        this.isLoading = true;
        const result =
          this.id !== "criar"
            ? await this.updateCategory({ category: objCategory, id: this.id })
            : await this.createCategory({ category: objCategory });

        this.isLoading = false;
        return result;
      }
    },

    validationBeforeRequest() {
      const msg = [];
      if (!this.categoryName.length) {
        msg.push(this.General.TYPE_NAME);
      }
      if (!this.prioritySelected.id) {
        msg.push(this.General.REQUIRED_PRIORITY_LEVEL);
      }
      if (!this.secrecySelected.id) {
        msg.push(this.General.REQUIRED_ACCESS_LEVEL);
      }
      if (this.isConfidential && !this.confidentialityPeriod.ref) {
        msg.push(this.General.SELECT_LEVEL_PERIOD);
      }

      if (msg.length) {
        this.snackToast.open({
          message: `${this.General.REQUIRED_FIELDS}: ${msg.join(", ")}.`,
          type: "is-danger",
          duration: 8000
        });
      }

      return !msg.length;
    },

    async cloneCategory(category, search = true) {
      if (!category) return false;
      let option;
      if (search) {
        this.isLoading = true;
        option = await this.categoryRequestById(category.id);
        this.isLoading = false;
      } else option = category;
      if (option.priority.id) this.prioritySelected = option.priority;
      if (option.secrecy.id) this.secrecySelected = option.secrecy;
      if (option.description) this.descriptionCategory = option.description;
      if (option.indexers) this.indexCategory = option.indexers;
      if (option.name) this.categoryName = option.name;
      if (option.block_param_low_level)
        this.blockSecrecyDown = option.block_param_low_level;
      if (option.low_level_responsible)
        this.lowLevelResponsible = option.low_level_responsible;
      if (option.open_process) this.openAnotherSystem = option.open_process;
      if (option.legal_reference.length)
        this.legal_reference = this.getLegalReference.filter(legalReference =>
          option.legal_reference.some(
            legalReferenceReceived =>
              legalReferenceReceived.id === legalReference.id
          )
        );
      if (option.coverage_to_use) {
        const partIds = option.coverage_to_use.map(part => part.part_id);
        this.part = this.getParts.filter(part => partIds.includes(part.id));
      }
      if (option.all_parts) this.allParts = option.all_parts;
      if (option.confidentiality_period) {
        this.$nextTick(() => {
          this.updateConfidentialityPeriod(option.confidentiality_period);
        });
      }
    },

    close() {
      this.$router.push("/app/admin/assuntos-categorias");
    },

    getSecreciesById(id) {
      return this.secrecies.find(option => option.id === id);
    },

    async newLegalReference(tag) {
      if (typeof tag === "string") {
        const exist = this.getFilteredLegalReference(tag).length;
        if (!exist) {
          const objTemp = {
            description: tag,
            secrecy: null
          };
          const result = await this.createLegalReference(objTemp);
          if (result) {
            this.legal_reference = [
              ...this.legal_reference,
              {
                ...result,
                description_trunc: objTemp.description.replace(/ \(.+/g, "")
              }
            ];
          } else this.legal_reference.splice(-1, 1);
        }
      }
    },

    async createOrUpdate(use = false) {
      const request = await this.create();
      if (request && request.status) {
        if (use)
          this.$router.push({
            path: "/app/admin/subcategoria/criar",
            query: { parent: request.result.id }
          });
        else this.close();
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";

.section {
  background-color: $color-1;
  padding: 20px 1.5rem 0 1.5rem;
  @media screen and (max-width: $tablet) {
    padding: 20px 0.75rem 0;
  }
}

.optional-label {
  font-size: 12px;
  font-weight: 300;
  color: $color-13;
}

.config-select {
  display: flex;
  min-width: 12vw;
  align-items: flex-start;
  justify-content: space-between;
  text-align: left;
}

.row {
  flex-direction: row;
  display: flex;
}

.full-width {
  width: 100%;
}

.custom-buttons {
  display: flex;

  @media screen and (max-width: $tablet) {
    flex-direction: column;
    gap: 10px;
    width: 100%;
    padding-right: 0 !important;
  }
}
</style>
